
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CalendarClock } from 'lucide-react';

// Define types for service requests
interface ServiceRequest {
  id: string;
  serviceName: string;
  category: string;
  status: 'pending' | 'approved' | 'rejected';
  requestDate: string;
  responseDate?: string;
  note?: string;
  adminNote?: string;
}

// Sample data for service requests
const sampleRequests: ServiceRequest[] = [
  {
    id: 'req-001',
    serviceName: 'Digital Smile Design',
    category: 'cosmetic',
    status: 'approved',
    requestDate: '2025-03-15',
    responseDate: '2025-03-18',
    note: 'This is a computer-aided smile design service',
    adminNote: 'Added to the cosmetic services category'
  },
  {
    id: 'req-002',
    serviceName: 'Ceramic Onlays',
    category: 'dental',
    status: 'pending',
    requestDate: '2025-04-05',
    note: 'Alternative to traditional fillings'
  },
  {
    id: 'req-003',
    serviceName: 'Sleep Apnea Devices',
    category: 'other',
    status: 'rejected',
    requestDate: '2025-03-01',
    responseDate: '2025-03-10',
    adminNote: 'This falls under medical devices category, not dental services'
  }
];

const getStatusColor = (status: ServiceRequest['status']) => {
  switch (status) {
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const ServiceRequestHistory: React.FC = () => {
  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-lg">Service Request History</CardTitle>
      </CardHeader>
      <CardContent>
        {sampleRequests.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">No service requests yet</p>
        ) : (
          <div className="space-y-4">
            {sampleRequests.map((request) => (
              <div key={request.id} className="border rounded-md p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium">{request.serviceName}</h3>
                    <p className="text-sm text-muted-foreground">Category: {request.category}</p>
                  </div>
                  <Badge className={getStatusColor(request.status)}>
                    {request.status}
                  </Badge>
                </div>
                
                <div className="flex items-center mt-2 text-sm text-muted-foreground">
                  <CalendarClock className="h-4 w-4 mr-1" />
                  <span>Requested on {new Date(request.requestDate).toLocaleDateString()}</span>
                </div>
                
                {request.note && (
                  <p className="mt-2 text-sm border-t pt-2">
                    <span className="font-medium">Your note:</span> {request.note}
                  </p>
                )}
                
                {request.adminNote && (
                  <p className="mt-2 text-sm bg-muted p-2 rounded">
                    <span className="font-medium">Admin response:</span> {request.adminNote}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ServiceRequestHistory;
